import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Layouts
import CustomerLayout from '../layouts/CustomerLayout';
import AdminLayout from '../layouts/AdminLayout';
import AuthLayout from '../layouts/AuthLayout';

// Auth Pages - Regular imports (smaller, frequently accessed)
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ConfirmEmail from '../pages/auth/ConfirmEmail';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';

// Customer Pages - Lazy loaded for performance
const Home = lazy(() => import('../pages/customer/Home'));
const About = lazy(() => import('../pages/customer/About'));
const Products = lazy(() => import('../pages/customer/Products'));
const Category = lazy(() => import('../pages/customer/Category'));
const ProductDetails = lazy(() => import('../pages/customer/ProductDetails'));
const News = lazy(() => import('../pages/customer/News'));
const NewsDetails = lazy(() => import('../pages/customer/NewsDetails'));
const Contact = lazy(() => import('../pages/customer/Contact'));
const Profile = lazy(() => import('../pages/customer/Profile'));
const CartPage = lazy(() => import('../pages/customer/Cart'));
const Checkout = lazy(() => import('../pages/customer/Checkout'));
const PaymentSuccess = lazy(() => import('../pages/customer/PaymentSuccess'));
const PaymentFailed = lazy(() => import('../pages/customer/PaymentFailed'));
const CustomerOrderDetails = lazy(() => import('../pages/customer/CustomerOrderDetails'));
const OrdersHistory = lazy(() => import('../pages/customer/OrdersHistory'));
const Favorites = lazy(() => import('../pages/customer/Favorites'));
const SupportMessages = lazy(() => import('../pages/customer/SupportMessages'));
const NotificationCenter = lazy(() => import('../pages/customer/NotificationCenter'));
const Terms = lazy(() => import('../pages/customer/Terms'));
const Privacy = lazy(() => import('../pages/customer/Privacy'));
const FAQ = lazy(() => import('../pages/customer/FAQ'));

// Admin Pages - Lazy loaded for performance
const AdminDashboard = lazy(() => import('../pages/admin/Dashboard'));
const AdminProducts = lazy(() => import('../pages/admin/Products'));
const AdminCategories = lazy(() => import('../pages/admin/Categories'));
const AdminUsers = lazy(() => import('../pages/admin/Users'));
const OrdersManagement = lazy(() => import('../pages/admin/OrdersManagement'));
const OrderDetails = lazy(() => import('../pages/admin/OrderDetails'));
const AdminBlogs = lazy(() => import('../pages/admin/Blogs'));
const BlogEdit = lazy(() => import('../pages/admin/BlogEdit'));
const AdminSupport = lazy(() => import('../pages/admin/Support'));
const AdminNotificationCenter = lazy(() => import('../pages/admin/NotificationCenter'));
const AdminFAQs = lazy(() => import('../pages/admin/FAQs'));
const AdminSettings = lazy(() => import('../pages/admin/Settings'));

// Loading Component for Suspense
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRouter: React.FC = () => {
  const { user } = useAuth();

  return (
    <Router>
      <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={
          user ? <Navigate to={user.role === 'admin' ? '/admin' : '/'} replace /> : 
          <AuthLayout><Login /></AuthLayout>
        } />
        <Route path="/register" element={
          user ? <Navigate to="/" replace /> : 
          <AuthLayout><Register /></AuthLayout>
        } />
        <Route path="/confirm-email" element={<AuthLayout><ConfirmEmail /></AuthLayout>} />
        <Route path="/forgot-password" element={<AuthLayout><ForgotPassword /></AuthLayout>} />
        <Route path="/reset-password" element={<AuthLayout><ResetPassword /></AuthLayout>} />

        {/* Customer Routes */}
        <Route path="/" element={<CustomerLayout />}>
          <Route index element={
            <Suspense fallback={<PageLoader />}>
              <Home />
            </Suspense>
          } />
          <Route path="about" element={
            <Suspense fallback={<PageLoader />}>
              <About />
            </Suspense>
          } />
          <Route path="products" element={
            <Suspense fallback={<PageLoader />}>
              <Products />
            </Suspense>
          } />
          <Route path="category/:slug" element={
            <Suspense fallback={<PageLoader />}>
              <Category />
            </Suspense>
          } />
          <Route path="products/:id" element={
            <Suspense fallback={<PageLoader />}>
              <ProductDetails />
            </Suspense>
          } />
          <Route path="cart" element={
            <Suspense fallback={<PageLoader />}>
              <CartPage />
            </Suspense>
          } />
          <Route path="checkout" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Checkout />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="payment/success" element={
            <Suspense fallback={<PageLoader />}>
              <PaymentSuccess />
            </Suspense>
          } />
          <Route path="payment/failed" element={
            <Suspense fallback={<PageLoader />}>
              <PaymentFailed />
            </Suspense>
          } />
          <Route path="orders" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <OrdersHistory />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="orders/:id" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <CustomerOrderDetails />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="favorites" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Favorites />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="support-messages" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <SupportMessages />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="notifications" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <NotificationCenter />
              </Suspense>
            </ProtectedRoute>
          } />
          <Route path="news" element={
            <Suspense fallback={<PageLoader />}>
              <News />
            </Suspense>
          } />
          <Route path="news/:id" element={
            <Suspense fallback={<PageLoader />}>
              <NewsDetails />
            </Suspense>
          } />
          <Route path="news/slug/:id" element={
            <Suspense fallback={<PageLoader />}>
              <NewsDetails />
            </Suspense>
          } />
          <Route path="contact" element={
            <Suspense fallback={<PageLoader />}>
              <Contact />
            </Suspense>
          } />
          <Route path="terms" element={
            <Suspense fallback={<PageLoader />}>
              <Terms />
            </Suspense>
          } />
          <Route path="privacy" element={
            <Suspense fallback={<PageLoader />}>
              <Privacy />
            </Suspense>
          } />
          <Route path="faq" element={
            <Suspense fallback={<PageLoader />}>
              <FAQ />
            </Suspense>
          } />
          <Route path="profile" element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <Profile />
              </Suspense>
            </ProtectedRoute>
          } />
        </Route>

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute adminOnly>
            <AdminLayout />
          </ProtectedRoute>
        }>
          <Route index element={
            <Suspense fallback={<PageLoader />}>
              <AdminDashboard />
            </Suspense>
          } />
          <Route path="products" element={
            <Suspense fallback={<PageLoader />}>
              <AdminProducts />
            </Suspense>
          } />
          <Route path="categories" element={
            <Suspense fallback={<PageLoader />}>
              <AdminCategories />
            </Suspense>
          } />
          <Route path="users" element={
            <Suspense fallback={<PageLoader />}>
              <AdminUsers />
            </Suspense>
          } />
          <Route path="orders" element={
            <Suspense fallback={<PageLoader />}>
              <OrdersManagement />
            </Suspense>
          } />
          <Route path="orders/:id" element={
            <Suspense fallback={<PageLoader />}>
              <OrderDetails />
            </Suspense>
          } />
          <Route path="blogs" element={
            <Suspense fallback={<PageLoader />}>
              <AdminBlogs />
            </Suspense>
          } />
          <Route path="blogs/new" element={
            <Suspense fallback={<PageLoader />}>
              <BlogEdit />
            </Suspense>
          } />
          <Route path="blogs/:id" element={
            <Suspense fallback={<PageLoader />}>
              <BlogEdit />
            </Suspense>
          } />
          <Route path="support" element={
            <Suspense fallback={<PageLoader />}>
              <AdminSupport />
            </Suspense>
          } />
          <Route path="notifications" element={
            <Suspense fallback={<PageLoader />}>
              <AdminNotificationCenter />
            </Suspense>
          } />
          <Route path="faqs" element={
            <Suspense fallback={<PageLoader />}>
              <AdminFAQs />
            </Suspense>
          } />
          <Route path="settings" element={
            <Suspense fallback={<PageLoader />}>
              <AdminSettings />
            </Suspense>
          } />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
